import React, { useState } from "react";
import { useAuth } from "@/hooks/use-auth";
import { Loader2 } from "lucide-react";
import SideNav from "./side-nav";
import TopNav from "./top-nav";
import MobileNav from "./mobile-nav";
import { log } from "@/lib/logger";

interface AppLayoutProps {
  children: React.ReactNode;
}

const AppLayout = React.memo(function AppLayout({ children }: AppLayoutProps) {
  const { isLoading: authLoading, user, error: authError } = useAuth();
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  
  log.render("AppLayout", { userEmail: user?.email });
  
  if (authLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
        <p className="text-sm text-gray-500">Loading your account...</p>
      </div>
    );
  }
  
  if (authError && !user) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4">
        <div className="bg-red-50 border border-red-200 rounded-md p-4 max-w-md">
          <h2 className="text-lg font-medium text-red-800 mb-2">Authentication Error</h2>
          <p className="text-sm text-red-700">{authError.message}</p>
          <p className="text-sm text-gray-500 mt-4">
            Try refreshing the page or clearing your browser cache.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Sidebar navigation - hidden on mobile */}
      <SideNav />
      
      {/* Mobile navigation - shown only when toggled */}
      <MobileNav 
        isOpen={isMobileNavOpen} 
        onClose={() => setIsMobileNavOpen(false)} 
      />
      
      {/* Main content area */}
      <div className="flex flex-col flex-1 overflow-hidden">
        {/* Top navigation */}
        <TopNav onOpenMobileNav={() => setIsMobileNavOpen(true)} />
        
        {/* Page content */}
        <main className="flex-1 overflow-y-auto focus:outline-none">
          {children}
        </main>
      </div>
    </div>
  );
});

export default AppLayout;
